/**
 * The names a body will carry once packaged for a World family.
 *
 * World detects a body's family by its skin joints' NAMES; the Package door
 * renames them to the family's canonical skeleton (`canonicalizeForFamily`).
 * So the panel and the door must ask World's question of the names the
 * package WILL carry, not the session's: an ActorCore kobold spells its root
 * `BoneRoot` and would otherwise refuse a family its package satisfies.
 *
 * Depends on: kernel (family-canon). Depended on by: main.ts (the package
 * panel and door), tests.
 */
import { FAMILY_CANONS, planFamilyRename, type FamilyCanon, type RigDescriptor } from '@harryhausen/kernel';

/** The session's BBSA mapping, keyed by the rig's own bone names. */
export function ontologyOfRig(rig: Pick<RigDescriptor, 'mapping'>): Map<string, string> {
  return new Map(rig.mapping.map((m) => [m.rigBone, m.ontologyId]));
}

/**
 * The skin-joint names to detect the family on: the first canon (World's
 * family order is the canon order — one today) whose plan has no refusal
 * gives its canonical names; a rig no canon can take keeps its own names, and
 * World's detection then says what it lacks.
 */
export function packageBoneNames(rig: Pick<RigDescriptor, 'bones' | 'mapping'>, canons: readonly FamilyCanon[] = Object.values(FAMILY_CANONS)): string[] {
  const onto = ontologyOfRig(rig);
  for (const canon of canons) {
    const plan = planFamilyRename(rig.bones, onto, canon);
    if (plan.refusals.length === 0) return [...plan.names];
  }
  return rig.bones.map((b) => b.name);
}
